import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import WebsiteScrape from '../Pages/WebsiteScrape';
import ChatBotProfile from '../Pages/ChatBotProfile';
import KnowledgeBaseSetMessage from '../Pages/KnowledgeBaseSetMessage';
import CustomizeWidget from '../Pages/CustomizeWidget';
import BULB_ICON from '../assets/icons/ComniqaiIcon-V3.0/bulb-icon.svg';
import KNOWLEDGEHUB_ACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/knowledgehub-activeicon.svg';
import KNOWLEDGEHUB_DEACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/knowledgehub-deactiveicon.svg';
import COMPANY_ACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/company-activeicon.svg';
import COMPANY_DEACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/company-deactiveicon.svg';
import BOTPROFILE_ACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/bot-activeicon.svg';
import BOTPROFILE_DEACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/bot-deactiveicon.svg';
import WIDGET_ACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/widget-activeicon.svg';
import WIDGET_DEACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/widget-deactiveicon.svg';
import FAQ_ACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/faq-activeicon.svg';
import FAQ_DEACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/faq-deactiveicon.svg';
import LEAP_ACTIVEICON from '../assets/icons/ComniqaiIcon-V3.0/leap-deactiveicon.svg';
import TESTAREA_ICON from '../assets/icons/ComniqaiIcon-V3.0/testing-area-icon.svg';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import FAQManager from '../Pages/FAQManager';
import OnboardingPopup from './OnboardingPopup';
import AIChatTestLab from '../Pages/AIChatTestLab';
import SubContext from '../contexts/SubContext';
import { FaBolt, FaCrown, FaDollarSign } from 'react-icons/fa';
import { getPlanLevel, PLAN_LEVELS, planIcons } from '../API/Constant';

// Tab Component to handle individual tabs
const Tab = ({ tab, activeTrainTab, handleTabClick }) => {
  return (
    <div
      className={`tab-container-tab d-flex align-items-center position-relative ${tab.activeStep === activeTrainTab ? 'tab-container-tab--active' : ''} ${
        tab.gradient ? 'tab-container-tab--gradient' : ''
      }`}
      onClick={() => handleTabClick(tab.tabQueryParam)}
      style={{ cursor: tab.disabled ? 'not-allowed' : 'pointer', pointerEvents: tab.disabled ? 'none' : 'auto' }}
    >
      <img src={tab.activeStep === activeTrainTab ? tab.activeIcon : tab.deActiveIcon} alt={`${tab.label}-icon`} className='me-2' style={{ width: '16px', height: '16px' }} />
      <span className='tab-container-label outfit-regular'>{tab.label}</span>
      {tab.badge && <span className='tab-container-badge rounded-pill text-white outfit-regular'>{tab.badge}</span>}
    </div>
  );
};

// Main TrainAgent Component
const TrainAgent = ({ profileDetails, getUserProfileDetails }) => {
  const [activeTrainTab, setActiveTrainTab] = useState('knowledge-hub');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState({ show: false, requiredPlan: '', tabName: '' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subsFlag = useContext(SubContext);
  const subscriptionInfo = subsFlag?.subscriptionFlag?.user?.subscription ?? null;
  const subscriptionName = subscriptionInfo?.name ?? 'Free';
  const planLevel = useMemo(() => getPlanLevel(subscriptionName), [subscriptionName]);

  const tabs = [
    { label: 'Knowledge Hub', activeIcon: KNOWLEDGEHUB_ACTIVEICON, deActiveIcon: KNOWLEDGEHUB_DEACTIVEICON, activeStep: 'knowledge-hub', tabQueryParam: 'knowledge-hub' },
    { label: 'FAQs', activeIcon: FAQ_ACTIVEICON, deActiveIcon: FAQ_DEACTIVEICON, activeStep: 'faqs', tabQueryParam: 'faqs' },
    { label: 'Company', activeIcon: COMPANY_ACTIVEICON, deActiveIcon: COMPANY_DEACTIVEICON, activeStep: 'company', tabQueryParam: 'company' },
    { label: 'Bot Profile', activeIcon: BOTPROFILE_ACTIVEICON, deActiveIcon: BOTPROFILE_DEACTIVEICON, activeStep: 'bot-profile', tabQueryParam: 'bot-profile' },
    { label: 'Widget', activeIcon: WIDGET_ACTIVEICON, deActiveIcon: WIDGET_DEACTIVEICON, activeStep: 'widget', tabQueryParam: 'widget' },
    { label: 'LEAP', activeIcon: LEAP_ACTIVEICON, deActiveIcon: LEAP_ACTIVEICON, badge: 'NEW', activeStep: 'leap', tabQueryParam: 'leap' },
    { label: 'Testing Area', gradient: true, activeIcon: TESTAREA_ICON, deActiveIcon: TESTAREA_ICON, activeStep: 'testing-area', tabQueryParam: 'testing-area' },
  ];

  const handleTabClick = useCallback(
    (tab) => {
      const currentPlanLevel = getPlanLevel(subscriptionName);

      if (tab === 'leap') {
        const requiredPlanLevel = PLAN_LEVELS.Growth;
        if (currentPlanLevel >= requiredPlanLevel) {
          navigate('/leap');
        } else {
          setShowUpgradeModal({ show: true, requiredPlan: 'Growth', tabName: 'LEAP' });
        }
      } else if (tab === 'testing-area') {
        const requiredPlanLevel = PLAN_LEVELS.Scale;
        if (currentPlanLevel >= requiredPlanLevel) {
          navigate(`/chatbot?tab=testing-area`);
        } else {
          setShowUpgradeModal({ show: true, requiredPlan: 'Scale', tabName: 'Testing Area' });
        }
      } else {
        navigate(`/chatbot?tab=${tab}`);
        setActiveTrainTab(tab);
      }
    },
    [subscriptionName, navigate]
  );

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      if (!planLevel) return;
      if (tabParam === 'testing-area' && planLevel < PLAN_LEVELS.Scale) {
        setShowUpgradeModal({ show: true, requiredPlan: 'Scale', tabName: 'Testing Area' });
        setActiveTrainTab('knowledge-hub');
        navigate('/chatbot?tab=knowledge-hub', { replace: true });
        return;
      }
      setActiveTrainTab(tabParam);
    }
    const isExactChatbotURL = window.location.pathname === '/chatbot' && window.location.search === '';
    if (isExactChatbotURL && profileDetails?.is_first_login) {
      setShowOnboarding(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!subscriptionName) return;
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTrainTab(tabParam);
      const currentPlanLevel = getPlanLevel(subscriptionName);
      if (!currentPlanLevel) return;
      if (tabParam === 'leap' && currentPlanLevel < PLAN_LEVELS.Growth) {
        setShowUpgradeModal({ show: true, requiredPlan: 'Growth', tabName: 'LEAP' });
      }

      if (tabParam === 'testing-area' && currentPlanLevel < PLAN_LEVELS.Scale) {
        setShowUpgradeModal({ show: true, requiredPlan: 'Scale', tabName: 'Testing Area' });
        setActiveTrainTab('knowledge-hub');
      } else if (tabParam === 'testing-area') {
        setActiveTrainTab('testing-area');
      } else {
        setActiveTrainTab(tabParam);
      }
    }

    const isExactChatbotURL = window.location.pathname === '/chatbot' && window.location.search === '';
    if (isExactChatbotURL && profileDetails?.is_first_login) {
      setShowOnboarding(true);
    }
  }, [searchParams, profileDetails, subscriptionName]);

  const handleUpgradeModalClose = () => {
    setShowUpgradeModal(false);
  };

  const handleUpgradeClick = () => {
    setShowUpgradeModal(false);
    navigate('/pricing');
  };

  const renderTabContent = () => {
    switch (activeTrainTab) {
      case 'knowledge-hub':
        return <WebsiteScrape refreshUserProfile={getUserProfileDetails} />;
      case 'company':
        return <ChatBotProfile />;
      case 'bot-profile':
        return <KnowledgeBaseSetMessage />;
      case 'widget':
        return <CustomizeWidget />;
      case 'faqs':
        return <FAQManager />;
      case 'testing-area':
        return <AIChatTestLab />;
      default:
        return <WebsiteScrape />;
    }
  };

  return (
    <>
      {showOnboarding && (
        <OnboardingPopup
          onClose={() => {
            setShowOnboarding(false);
          }}
          refreshUserProfile={getUserProfileDetails}
        />
      )}
      <div className='row m-0 mt-2 mb-4 ps-md-5 ps-sm-4 ps-4 pe-md-5 pe-sm-4 pe-4 '>
        <div className='col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 p-0'>
          <div className='chatbox-field'>
            <div className='container-fluid min-vh-100 p-0'>
              <div className='train-box d-flex align-items-center'>
                <div className='train-box-icon-container d-flex justify-content-center align-items-center me-3'>
                  <img src={BULB_ICON} alt='train-icon' />
                </div>
                <div>
                  <h5 className='outfit-bold text-primary-gradient mb-1'>Train ComniqAI</h5>
                  <p className='train-box-subtitle text-muted mb-0 outfit-regular'>Configure your AI assistant to deliver exceptional customer experiences</p>
                </div>
              </div>

              {/* Tabs */}
              <div className='tab-container my-4 d-flex gap-2 flex-wrap'>
                {tabs.map((tab, index) => (
                  <Tab key={index} tab={tab} activeTrainTab={activeTrainTab} handleTabClick={handleTabClick} />
                ))}
              </div>

              {/* Tab Content */}
              <div>{renderTabContent()}</div>
            </div>
          </div>
        </div>
      </div>

      {showUpgradeModal.show && (
        <div className='custom-popup d-flex align-items-center justify-content-center'>
          <div className='popup-content shadow-lg rounded-4 p-4 bg-white' style={{ maxWidth: '500px', width: '100%' }}>
            {/* Header */}
            <div className='text-center mb-3'>
              <FaBolt size={30} className='text-warning mb-3' />
              <h4 className='outfit-bold mb-2'> {showUpgradeModal.tabName} Access</h4>
              <p className='outfit-regular text-muted small mb-0'>Unlock powerful features with the {showUpgradeModal.requiredPlan} & above plans</p>
            </div>

            {/* Body */}
            <div className='text-center mb-3'>
              <p className='outfit-regular font16 mb-2'>
                Your current plan: <b>{subscriptionName || 'Free'}</b>
              </p>
              <div className='alert alert-info d-flex align-items-center justify-content-center gap-2 p-2 my-3 rounded-3'>
                <FaCrown size={28} className='text-warning' />
                <span className='outfit-regular font14 mb-0'>
                  {showUpgradeModal.tabName} is available only on the <b>{showUpgradeModal.requiredPlan} & above plans</b>.
                </span>
              </div>
            </div>

            <hr />

            {/* Footer */}
            <div className='popup-footer d-flex justify-content-center gap-2'>
              <button className='btn btn-outline-secondary outfit-medium px-4 rounded-pill' onClick={handleUpgradeModalClose}>
                Maybe Later
              </button>
              <button className='btn-generatedlead outfit-medium px-4 rounded-pill text-white' onClick={handleUpgradeClick}>
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TrainAgent;
